/**
 * YouTube Subscription Service
 * Handles YouTube channel subscriptions and video imports using Supabase
 */

import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";

export interface YouTubeSubscription {
  id: string;
  user_id: string;
  channel_id: string;
  channel_name: string;
  channel_url: string | null;
  channel_thumbnail: string | null;
  subscriber_count: string | null;
  sync_frequency: string;
  auto_import: boolean;
  ai_enhance: boolean;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
}

export interface ImportJob {
  id: string;
  user_id: string;
  source_type: string;
  external_id: string | null;
  title: string | null;
  status: string;
  progress: string | null;
  progress_message: string | null;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ChannelSearchResult {
  channelId: string;
  channelName: string;
  channelUrl: string;
  channelThumbnail: string;
  subscriberCount: string;
  description: string;
}

class YouTubeSubscriptionService {
  private async getCurrentUserId(): Promise<string> {
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error("User not authenticated. Please log in.");
    }
    return userId;
  }

  /**
   * Search for YouTube channels by name.
   * Calls backend /api/youtube/search-channels which uses YOUTUBE_API_KEY from .env
   */
  async searchChannels(query: string): Promise<ChannelSearchResult[]> {
    const q = (query || "").trim();
    if (!q) return [];

    const url = `/api/youtube/search-channels?q=${encodeURIComponent(q)}`;
    const res = await fetch(url);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err?.error || `Channel search failed (${res.status})`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }

  /**
   * Get all subscriptions for the current user
   */
  async getSubscriptions(): Promise<YouTubeSubscription[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("youtube_subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      if (error.code === "PGRST116" || error.message.includes("does not exist") || error.code === "42P01") {
        console.warn("YouTube subscriptions table not yet created. Run the migration to enable this feature.");
        return [];
      }
      throw new Error(`Failed to fetch subscriptions: ${error.message}`);
    }

    return (data || []) as YouTubeSubscription[];
  }

  /**
   * Create a new subscription
   */
  async createSubscription(subscriptionData: {
    channelId: string;
    channelName: string;
    channelUrl?: string;
    channelThumbnail?: string;
    subscriberCount?: string;
    syncFrequency: string;
    autoImport: boolean;
    aiEnhance: boolean;
  }): Promise<YouTubeSubscription> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    // Check if subscription already exists
    const { data: existing } = await supabase
      .from("youtube_subscriptions")
      .select("id")
      .eq("user_id", userId)
      .eq("channel_id", subscriptionData.channelId)
      .single();

    if (existing) {
      throw new Error("You are already subscribed to this channel");
    }

    const { data, error } = await supabase
      .from("youtube_subscriptions")
      .insert({
        user_id: userId,
        channel_id: subscriptionData.channelId,
        channel_name: subscriptionData.channelName,
        channel_url: subscriptionData.channelUrl || null,
        channel_thumbnail: subscriptionData.channelThumbnail || null,
        subscriber_count: subscriptionData.subscriberCount || null,
        sync_frequency: subscriptionData.syncFrequency,
        auto_import: subscriptionData.autoImport,
        ai_enhance: subscriptionData.aiEnhance,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      if (error.code === "PGRST116" || error.message.includes("does not exist") || error.code === "42P01") {
        throw new Error("YouTube subscriptions table not yet created. Please run the migration first.");
      }
      throw new Error(`Failed to create subscription: ${error.message}`);
    }

    return data as YouTubeSubscription;
  }

  /**
   * Delete a subscription
   */
  async deleteSubscription(id: string): Promise<void> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("youtube_subscriptions")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      throw new Error(`Failed to delete subscription: ${error.message}`);
    }
  }

  /**
   * Update subscription settings
   */
  async updateSubscription(
    id: string,
    updates: Partial<{
      syncFrequency: string;
      autoImport: boolean;
      aiEnhance: boolean;
      isActive: boolean;
    }>
  ): Promise<YouTubeSubscription> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const updateData: any = {};
    if (updates.syncFrequency !== undefined) updateData.sync_frequency = updates.syncFrequency;
    if (updates.autoImport !== undefined) updateData.auto_import = updates.autoImport;
    if (updates.aiEnhance !== undefined) updateData.ai_enhance = updates.aiEnhance;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      .from("youtube_subscriptions")
      .update(updateData)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update subscription: ${error.message}`);
    }

    return data as YouTubeSubscription;
  }

  /**
   * Sync a subscription - check for new videos and import them
   * Returns the number of new videos imported
   */
  async syncSubscription(id: string): Promise<{ newVideos: number }> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    // Get the subscription
    const { data: subscription, error: fetchError } = await supabase
      .from("youtube_subscriptions")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .single();

    if (fetchError || !subscription) {
      throw new Error("Subscription not found");
    }

    // Update last_synced_at
    await supabase
      .from("youtube_subscriptions")
      .update({ last_synced_at: new Date().toISOString() })
      .eq("id", id);

    // In a real implementation, you would:
    // 1. Fetch new videos from YouTube Data API
    // 2. Check which ones are already imported (check sources table by externalId)
    // 3. Import new videos using youtubeService
    // 4. Return count of new videos

    // For now, return 0 - this needs YouTube Data API integration
    console.warn("Sync subscription requires YouTube Data API implementation");
    return { newVideos: 0 };
  }

  /**
   * Get all import jobs for the current user
   */
  async getImportJobs(): Promise<ImportJob[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("youtube_import_jobs")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      if (error.code === "PGRST116" || error.message.includes("does not exist") || error.code === "42P01") {
        console.warn("YouTube import jobs table not yet created.");
        return [];
      }
      throw new Error(`Failed to fetch import jobs: ${error.message}`);
    }

    return (data || []) as ImportJob[];
  }

  /**
   * Create an import job
   */
  async createImportJob(jobData: {
    sourceType: string;
    externalId?: string;
    title?: string;
    status?: string;
  }): Promise<ImportJob> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("youtube_import_jobs")
      .insert({
        user_id: userId,
        source_type: jobData.sourceType,
        external_id: jobData.externalId || null,
        title: jobData.title || null,
        status: jobData.status || "pending",
      })
      .select()
      .single();

    if (error) {
      if (error.code === "PGRST116" || error.message.includes("does not exist") || error.code === "42P01") {
        throw new Error("YouTube import jobs table not yet created. Please run the migration first.");
      }
      throw new Error(`Failed to create import job: ${error.message}`);
    }

    return data as ImportJob;
  }

  /**
   * Update an import job
   */
  async updateImportJob(
    id: string,
    updates: Partial<{
      status: string;
      progress: string;
      progressMessage: string;
      errorMessage: string;
      completedAt: string;
    }>
  ): Promise<ImportJob> {
    if (!isSupabaseConfigured) {
      throw new Error("Supabase not configured");
    }

    const userId = await this.getCurrentUserId();

    const updateData: any = {};
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.progress !== undefined) updateData.progress = updates.progress;
    if (updates.progressMessage !== undefined) updateData.progress_message = updates.progressMessage;
    if (updates.errorMessage !== undefined) updateData.error_message = updates.errorMessage;
    if (updates.completedAt !== undefined) updateData.completed_at = updates.completedAt;

    if (updates.status === "processing" && !updateData.started_at) {
      updateData.started_at = new Date().toISOString();
    }

    const { data, error } = await supabase
      .from("youtube_import_jobs")
      .update(updateData)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update import job: ${error.message}`);
    }

    return data as ImportJob;
  }
}

export const youtubeSubscriptionService = new YouTubeSubscriptionService();






